/**
 * FeedbackKpiHeader — the metrics strip above the /feedback queue.
 *
 * Six tiles, in the shape `dealsKpiHeader.jsx` established (one fit-content
 * card, divider-separated Statistics, a chevron that remembers itself, a
 * refresh):
 *
 *   1. Open              — not resolved / won't fix / duplicate
 *   2. Untriaged         — still `new`; nobody has looked at it
 *   3. In progress       — someone is actually on it
 *   4. Open bugs         — open AND kind = bug
 *   5. High / Critical   — open AND severity in (high, critical)
 *   6. Oldest open       — age in days of the longest-waiting open ticket
 *
 * WHY IT TAKES ROWS INSTEAD OF FETCHING
 * There is a `GET /feedback_requests/stats` endpoint, and it is the obvious
 * source — but it ignores the `mine` parameter, so an admin who switches the
 * page to "Mine" would get counts over everyone's tickets sitting above a
 * grid of their own. The list the page already holds is scoped correctly for
 * both audiences and is at most a few hundred rows, so the arithmetic is
 * free and cannot disagree with the grid.
 *
 * `rows` must be the WHOLE scoped queue, not the filtered view — that is why
 * the page stopped passing `open_only` to the server. Tile 1 reading "Open:
 * 12" out of 12 visible rows tells you nothing; reading it out of 40 tells
 * you where the queue stands. `visibleCount` drives the trailing "Showing X
 * of Y" line so the relationship between the two is on screen.
 */
import React, { useCallback, useMemo, useState } from "react";
import {
  Button,
  Card,
  Divider,
  Flex,
  Skeleton,
  Statistic,
  Tooltip,
  Typography,
  theme,
} from "antd";
import { DownOutlined, ReloadOutlined, RightOutlined } from "@ant-design/icons";
import { format } from "numerable";

import {
  FEEDBACK_KPI_COLLAPSED,
  readFlag,
  writeFlag,
} from "../utils/uiPrefs";
import { feedbackStats } from "../utils/feedbackStats";

/**
 * @param {object}   props
 * @param {Array}    props.rows          the whole scoped queue
 * @param {number}   [props.visibleCount] rows the grid is currently showing
 * @param {boolean}  [props.loading]
 * @param {string}   [props.errorMsg]
 * @param {Function} [props.onRefresh]
 */
const FeedbackKpiHeader = ({
  rows,
  visibleCount,
  loading = false,
  errorMsg = null,
  onRefresh,
}) => {
  const { token: t } = theme.useToken();

  const [collapsed, setCollapsed] = useState(() =>
    readFlag(FEEDBACK_KPI_COLLAPSED, false)
  );
  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      writeFlag(FEEDBACK_KPI_COLLAPSED, !prev);
      return !prev;
    });
  }, []);

  const stats = useMemo(() => feedbackStats(rows), [rows]);

  const tiles = useMemo(
    () => [
      {
        key: "open",
        title: "Open",
        value: format(stats.open, "0,0"),
        tooltip:
          "Tickets still on the queue: status is not Resolved, Won't fix or Duplicate.",
      },
      {
        key: "untriaged",
        title: "Untriaged",
        value: format(stats.untriaged, "0,0"),
        color: stats.untriaged > 0 ? t.colorWarningText : undefined,
        tooltip:
          "Open tickets still sitting at New — nobody has set a type, severity or status on them yet.",
      },
      {
        key: "inProgress",
        title: "In progress",
        value: format(stats.inProgress, "0,0"),
        tooltip:
          "Open tickets someone has picked up: status is In progress or Ready to re-test.",
      },
      {
        key: "openBugs",
        title: "Open bugs",
        value: format(stats.openBugs, "0,0"),
        tooltip:
          "Open tickets filed as a Bug. Features and questions are excluded.",
      },
      {
        key: "openUrgent",
        title: "High / Critical",
        value: format(stats.openUrgent, "0,0"),
        color: stats.openUrgent > 0 ? t.colorErrorText : undefined,
        tooltip:
          "Open tickets triaged High or Critical. Untriaged tickets carry no severity and are not counted here — check Untriaged too.",
      },
      {
        key: "oldestOpen",
        title: "Oldest open",
        // An empty queue has no oldest ticket; "0d" would read as "one filed
        // today", which is a different and wrong thing.
        value: stats.oldestOpen === null ? "—" : `${format(stats.oldestOpen, "0,0")}d`,
        color:
          stats.oldestOpen !== null && stats.oldestOpen > 30
            ? t.colorErrorText
            : stats.oldestOpen !== null && stats.oldestOpen > 14
              ? t.colorWarningText
              : undefined,
        tooltip:
          "Days since the longest-waiting open ticket was reported, by the reporter's clock.",
      },
    ],
    [stats, t.colorErrorText, t.colorWarningText]
  );

  // Same shell in every state, so the filter row below does not jump between
  // loading, loaded, collapsed and failed.
  const shell = (children, testId) => (
    <Card
      size="small"
      styles={{ body: { padding: "6px 10px" } }}
      style={{
        marginBottom: 8,
        marginInline: 4,
        width: "fit-content",
        maxWidth: "99%",
      }}
      data-testid={testId}
    >
      {children}
    </Card>
  );

  if (loading && (!rows || rows.length === 0)) {
    // Fixed width: a "100%" skeleton inside a fit-content card collapses to
    // nothing. Roughly where the six tiles settle, so the card does not
    // resize under the user when the numbers arrive.
    return shell(
      <div style={{ width: 720, maxWidth: "100%" }}>
        <Skeleton active title={{ width: 140 }} paragraph={{ rows: 1, width: "100%" }} />
      </div>,
      "feedback-kpi-loading"
    );
  }

  return shell(
    <Flex align="center" gap="small" wrap="wrap">
      <Flex align="center" gap={4} wrap="wrap" style={{ minWidth: 0 }}>
        <Button
          type="text"
          size="small"
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Show metrics" : "Hide metrics"}
          icon={collapsed ? <RightOutlined /> : <DownOutlined />}
          onClick={toggleCollapsed}
        />
        {collapsed ? (
          <Typography.Text type="secondary">Metrics</Typography.Text>
        ) : errorMsg ? (
          // Replaces the numbers rather than sitting above them: figures
          // computed from a half-loaded list are worse than no figures.
          <Typography.Text type="danger">Metrics unavailable</Typography.Text>
        ) : (
          tiles.map((tile, index) => (
            <React.Fragment key={tile.key}>
              {index > 0 && (
                <Divider orientation="vertical" style={{ height: 28, marginInline: 4 }} />
              )}
              <Tooltip title={tile.tooltip}>
                <Statistic
                  title={tile.title}
                  value={tile.value}
                  // `styles.content`, not the `valueStyle` dealsKpiHeader
                  // uses — antd 6 deprecated that and warns on every render.
                  styles={{ content: { fontSize: 18, color: tile.color } }}
                  style={{ paddingInline: 4 }}
                />
              </Tooltip>
            </React.Fragment>
          ))
        )}
      </Flex>

      <Flex align="center" gap="small" style={{ marginInlineStart: 4 }}>
        {!collapsed &&
          !errorMsg &&
          visibleCount !== undefined &&
          visibleCount !== (rows?.length ?? 0) && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Showing {format(visibleCount, "0,0")} of{" "}
              {format(rows?.length ?? 0, "0,0")}
            </Typography.Text>
          )}
        {onRefresh && (
          <Tooltip title="Reload the queue">
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              onClick={onRefresh}
              loading={loading}
              aria-label="Refresh feedback metrics"
            />
          </Tooltip>
        )}
      </Flex>
    </Flex>
  );
};

export default FeedbackKpiHeader;
